import React from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row } from 'react-bootstrap';
import Datetime from 'react-datetime';
import Select from 'react-select';
import { useDarkMode } from 'Hooks';
import { getReactSelectStyles } from 'Helpers/UiHelper';
import { DATE_FORMAT_WEB, DATETIME_FORMAT_ISO } from 'Constants/DateFormatConstants';
import { DateTimeFormater } from 'Helpers/DateTimeHelper';

const FlightDetailsSection = ({
  currentSelectedFlight,
  setCurrentSelectedFlight,
  validationFlightFormErrors,
  setValidationFlightFormErrors,
  mappedAirports,
  isDisabled,
}) => {
  const { darkMode } = useDarkMode();
  const departureAirports = currentSelectedFlight.departureAirport?.split(',');
  const arrivalAirports = currentSelectedFlight.arrivalAirport?.split(',');

  return (
    <>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>Confirmed Flight Date</Form.Label>
          <Datetime
            timeFormat={false}
            dateFormat={DATE_FORMAT_WEB}
            closeOnSelect
            utc={true}
            inputProps={{ placeholder: DATE_FORMAT_WEB, disabled: isDisabled }}
            value={DateTimeFormater(currentSelectedFlight.confirmedFlightDate, null, DATE_FORMAT_WEB)}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                confirmedFlightDate: DateTimeFormater(e, null, DATETIME_FORMAT_ISO),
              });
            }}
          />
          {validationFlightFormErrors.confirmedFlightDate && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {validationFlightFormErrors.confirmedFlightDate}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Booking Reference</Form.Label>
          <Form.Control
            type="text"
            value={currentSelectedFlight?.bookingReference || ''}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                bookingReference: e.target.value,
              });
            }}
            disabled={isDisabled}
            isInvalid={!!validationFlightFormErrors.bookingReference}
          />
          <Form.Control.Feedback type="invalid">{validationFlightFormErrors.bookingReference}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Flight Number</Form.Label>
          <Form.Control
            type="text"
            value={currentSelectedFlight.flightNumber || ''}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                flightNumber: e.target.value,
              });
            }}
            disabled={isDisabled}
            isInvalid={!!validationFlightFormErrors.flightNumber}
          />
          <Form.Control.Feedback type="invalid">{validationFlightFormErrors.flightNumber}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Via</Form.Label>
          <Form.Control
            type="text"
            value={currentSelectedFlight.via || ''}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                via: e.target.value,
              });
            }}
            disabled={isDisabled}
            placeholder="Optional stopover"
          />
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>Departure Airport</Form.Label>
          <Select
            isDisabled={isDisabled}
            options={mappedAirports}
            isSearchable
            isClearable
            value={departureAirports?.map((airport) => mappedAirports.find((x) => x.value === airport))}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                departureAirport: e?.value || '',
              });
            }}
            styles={getReactSelectStyles(darkMode)}
          />
          {validationFlightFormErrors.departureAirport && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {validationFlightFormErrors.departureAirport}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Flight Departure Time</Form.Label>
          <Form.Control
            type="time"
            value={currentSelectedFlight.flightDepartureTime || ''}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                flightDepartureTime: e.target.value,
              });
            }}
            disabled={isDisabled}
            isInvalid={!!validationFlightFormErrors.flightDepartureTime}
          />
          <Form.Control.Feedback type="invalid">{validationFlightFormErrors.flightDepartureTime}</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>Arrival Airport</Form.Label>
          <Select
            isDisabled={isDisabled}
            options={mappedAirports}
            isSearchable
            isClearable
            value={arrivalAirports?.map((airport) => mappedAirports.find((x) => x.value === airport))}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                arrivalAirport: e?.value || '',
              });
            }}
            styles={getReactSelectStyles(darkMode)}
          />
          {validationFlightFormErrors.arrivalAirport && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {validationFlightFormErrors.arrivalAirport}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Flight Arrival Time</Form.Label>
          <Form.Control
            type="time"
            value={currentSelectedFlight.flightArrivalTime || ''}
            onChange={(e) => {
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                flightArrivalTime: e.target.value,
              });
            }}
            disabled={isDisabled}
          />
        </Form.Group>
      </Row>
    </>
  );
};

FlightDetailsSection.propTypes = {
  currentSelectedFlight: PropTypes.object.isRequired,
  setCurrentSelectedFlight: PropTypes.func.isRequired,
  validationFlightFormErrors: PropTypes.object.isRequired,
  setValidationFlightFormErrors: PropTypes.func.isRequired,
  mappedAirports: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
};

export default FlightDetailsSection;
